import {
    fetchAllRates,
    getProviderRatesURL,
    SCENARIO_TYPES,
    PROVIDER_TYPES,
} from '@ratehub/mtg-common';

import { DEFAULT_ALL_PURCHASE_PARAMS, MORTGAGE_TYPES } from '../definitions/MortgageTerms';


async function fetchAllMortgageProviderRates({ params, providerType, providers, locale }) {
    const allProvidersRates = await Promise.all(params.map(
        param => fetchRatesByProviderAndType(param, providerType, providers, locale),
    ));

    return getAllRatesForProvider(
        allProvidersRates,
        params.map(param => ({
            type: param.type,
            term: param.term,
        })),
        locale,
    );
}

async function fetchRatesByProviderAndType(params, providerType, providers, locale) {
    return await fetchAllRates(SCENARIO_TYPES.PURCHASE, {
        ...DEFAULT_ALL_PURCHASE_PARAMS,
        type: params.type,
        term: params.term,
        city: params.city,
        province: params.province,
        isBigBank: providerType === PROVIDER_TYPES.BIG_BANK
            ? 1
            : 0,
        providerType: providerType !== PROVIDER_TYPES.BIG_BANK
            ? [ providerType ]
            : [],
        providers: providers
            ? providers
            : [],
        language: locale.substring(0, 2),
    });
}

function getAllRatesForProvider(result, typeTerms, locale) {
    const primeRate = result[0].primeRate;

    // Get all of the rates into one array to ease manipulation
    const allRates = result.reduce(
        (resultRates, rates) => {
            resultRates.push(...rates);
            return resultRates;
        }
        , [],
    );

    // Get the providers
    const uniqueProviders = result.reduce(
        (resultProviders, { providers }) =>
            Object.assign(resultProviders, providers)
        , {},
    );

    // Populate all providers with their rates for each type and term fetched (if exists)
    return Object.values(uniqueProviders)
        .map(provider => ({
            lastUpdatedDate: result[0].lastUpdated, // All result dates should be the same
            provider: {
                name: provider.name,
                slug: provider.slug,
                logo: provider.logo,
                id: provider.id,
                rating: provider?.rating,
                reviews: provider?.reviews,
                url: getProviderRatesURL({
                    providerSlug: provider.slug,
                    lenderType: provider.lenderType,
                }, locale),
            },
            rates: typeTerms.reduce((columns, header) => {
                const found = allRates.find(rate =>
                    rate.term === header.term
                    && rate.type === header.type
                    && rate.provider.slug === provider.slug,
                );

                // Regardless if the provider has a rate, populate the correct cell
                found
                    ? columns.push({
                        value: found.value,
                        href: found.href,
                        deltaPrime: found.type === MORTGAGE_TYPES.VARIABLE
                            ? getDifferenceFromPrime(primeRate, found.value)
                            : undefined,
                        rate: {
                            ...found,
                            provider,
                        },
                    })
                    : columns.push(null);

                return columns;
            }, []),
        }))
        // Don't include providers that have zero rates
        .filter(p => !p.rates.every(rate => rate === null));
}

function getDifferenceFromPrime(primeRate, rateValue) {
    return rateValue - primeRate;
}


export default fetchAllMortgageProviderRates;
